import {
  QButton,
  QFormControl,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  QTextarea,
} from '@qualio/ui-components';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MedtechUserV2 } from '../../../../../../../api/user';

type ChangedReviewer = {
  id: number;
  full_name: string;
};

type ChangedReviewersListProps = {
  users: ChangedReviewer[];
  label: 'Reviewers added' | 'Reviewers removed';
};

const ChangedReviewersList = ({ users, label }: ChangedReviewersListProps) => {
  const { t } = useTranslation();

  return (
    <QStack spacing="1">
      <QText fontWeight="semibold">
        {label === 'Reviewers added'
          ? t('labels.reviewersAdded')
          : t('labels.reviewersRemoved')}
      </QText>
      {users.length > 0 ? (
        users.map((user) => <QText key={user.id}>{user.full_name}</QText>)
      ) : (
        <QText>{t('labels.none')}</QText>
      )}
    </QStack>
  );
};

export type ManageReviewersConfirmationModalProps = {
  isOpen: boolean;
  modalTitle: string;
  addedReviewers: ChangedReviewer[];
  removedReviewers: ChangedReviewer[];
  selections: readonly MedtechUserV2[];
  requiresComment?: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onSubmit: (
    selections: readonly MedtechUserV2[],
    comment: string,
  ) => Promise<void>;
};

export const ManageReviewersConfirmationModal = ({
  isOpen,
  modalTitle,
  addedReviewers = [],
  removedReviewers = [],
  selections = [],
  requiresComment = false,
  onClose,
  onSubmit,
  onPrevious,
}: ManageReviewersConfirmationModalProps) => {
  const { t } = useTranslation();
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const textareaPlaceholder = useMemo(
    () =>
      removedReviewers.length > 1
        ? t('labels.reasonForRemovingReviewers')
        : t('labels.reasonForRemovingReviewer'),
    [removedReviewers.length, t],
  );

  const [reason, setReason] = useState('');

  const close = useCallback(() => {
    setReason('');
    onClose();
  }, [onClose]);

  const applyChanges = useCallback(async () => {
    setIsApplyingChanges(true);
    await onSubmit(selections, reason);
    setIsApplyingChanges(false);
  }, [onSubmit, reason, selections]);

  return (
    <QModal isOpen={isOpen} onClose={close}>
      <QModalHeader>
        <QText fontSize="xl">{modalTitle}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack spacing="6">
          <ChangedReviewersList
            users={addedReviewers}
            label={'Reviewers added'}
          />
          <ChangedReviewersList
            users={removedReviewers}
            label={'Reviewers removed'}
          />
          {requiresComment && (
            <QFormControl label={t('forms.reason')} isRequired>
              <QTextarea
                data-cy="manage-reviewer-modal-comment"
                placeholder={textareaPlaceholder}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
              />
            </QFormControl>
          )}
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton
          data-cy="document-manage-reviewers-cancel-button"
          variant="ghost"
          isDisabled={isApplyingChanges}
          onClick={close}
        >
          {t('buttons.cancel')}
        </QButton>
        <QButton
          data-cy="document-manage-reviewers-previous-button"
          variant="outline"
          isDisabled={isApplyingChanges}
          onClick={onPrevious}
        >
          {t('buttons.previous')}
        </QButton>
        <QButton
          data-cy="document-manage-reviewers-apply-changes-button"
          isLoading={isApplyingChanges}
          isDisabled={(requiresComment && !reason) || isApplyingChanges}
          onClick={applyChanges}
        >
          {t('buttons.applyChanges')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};
